import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, ModalBody, ModalFooter, Table } from 'reactstrap';
import { Button } from '@shoflo/polymer/components/Button/Button';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { clearModal } from 'actions';
import _ from 'underscore';
import styled from 'styled-components';
import { INVITE_CREW_MODAL } from 'constants/ModalTypes';
import StyledModalHeader from 'components/modal/StyledModalHeader';
import {
  StyledModalConfirmButton,
  StyledModalCancelButton,
} from 'components/modal/StyledModalButton';
import { getAvailableToolsLimit } from 'utils/teamHelper';
import {
  StyledSeatText,
  StyledSeatTitle,
  StyledSeatSummary,
  StyledSeatSummaryWrapper,
  StyledSelectWrapper,
  StyledHeading,
  StyledText,
} from './InviteCrewModal.styled';
import { getEligibleMembers, inviteCrewByEmail } from 'actions/Event';
import CrewListHeader from 'components/userTools/CrewListHeader';
import CrewSelect from 'components/CrewSelect.js';
import ListItem from 'components/userTools/CrewListItem';
import Roles from 'constants/Roles';

const StyledButton = styled(Button)`
  padding: 12px 16px;
  transition: all 0.2s ease-in-out;
  height: fit-content;
  align-self: center;
`;

const StyledTable = styled(Table)`
  overflow: scroll;
`;

export const customSelectStyles = {
  menu: (provided) => {
    return {
      ...provided,
      width: 'auto',
      maxWidth: 'calc(100% - 104px)', // allow space for `Add to queue` button
      display: 'flex',
    };
  },
};

export const InviteCrewModalUnconnected = (props) => {
  const {
    team,
    isOpen,
    clearModal,
    eventEligibleMembers,
    getEligibleMembers,
    isTeamAdmin,
    inviteCrewByEmail,
  } = props;

  const wasOpen = useRef();
  useEffect(() => {
    if (isOpen) {
      getEligibleMembers();
    }
    if (!isOpen && wasOpen.current) {
      setSelectedOptions([]);
    }
    wasOpen.current = isOpen;
  }, [isOpen, getEligibleMembers]);

  const [selectedOptions, setSelectedOptions] = useState([]);

  const { Rundown, Studio } = useMemo(() => {
    if (team) {
      return getAvailableToolsLimit(team);
    }
    return {};
  }, [team]);

  const closeModal = () => clearModal(INVITE_CREW_MODAL);

  const handleInviteCrewByEmail = () => {
    const users = _.map(selectedOptions, (options) => {
      const user = _.pick(options, 'email', 'role', 'availableTools');
      if (!user.role) user.role = Roles.ReadOnly;
      if (!user.availableTools) user.availableTools = [];
      return user;
    });

    inviteCrewByEmail(users);
  };

  return (
    <Modal isOpen={isOpen} toggle={closeModal}>
      <StyledModalHeader>Invite crew to Global Summit</StyledModalHeader>
      <ModalBody>
        <StyledSeatSummaryWrapper>
          <StyledSeatSummary>
            <StyledSeatTitle>Rundown</StyledSeatTitle>
            {Rundown && Rundown.limit !== 0 ? (
              <StyledSeatText>{Rundown.limit} seats available</StyledSeatText>
            ) : (
              <StyledSeatText>Unavailable on your plan!</StyledSeatText>
            )}
          </StyledSeatSummary>
          <StyledSeatSummary>
            <StyledSeatTitle>Studio</StyledSeatTitle>
            {Studio && Studio.limit !== 0 ? (
              <StyledSeatText>{Studio.limit} seats available</StyledSeatText>
            ) : (
              <StyledSeatText>Unavailable on your plan!</StyledSeatText>
            )}
          </StyledSeatSummary>
          {isTeamAdmin ? (
            <StyledButton
              color="secondary"
              variant="outlined"
              data-testid="upgradePlanBtn"
            >
              Upgrade plan
            </StyledButton>
          ) : null}
        </StyledSeatSummaryWrapper>
        <StyledSelectWrapper>
          <StyledHeading>Invite users</StyledHeading>
          <StyledText>
            Type or paste in a comma-separated list of emails. Press ENTER to
            queue users up, where you’ll be able to assign permissions and
            product access.
          </StyledText>
          <CrewSelect
            styles={customSelectStyles}
            isMulti
            options={eventEligibleMembers}
            onChange={setSelectedOptions}
            selectedOptions={selectedOptions}
            placeholder="e.g. mohammed@shoflo.tv"
            disableLabels={true}
          />
        </StyledSelectWrapper>
        {selectedOptions.length ? (
          <StyledTable>
            <CrewListHeader></CrewListHeader>
            <ListItem
              selectedOptions={selectedOptions}
              onChangeRoleClick={setSelectedOptions}
            />
          </StyledTable>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <StyledModalConfirmButton
          color="primary"
          onClick={handleInviteCrewByEmail}
          disabled={!selectedOptions.length}
          data-testid="sendInvitesButton"
        >
          Send invites
        </StyledModalConfirmButton>
        <StyledModalCancelButton
          outline
          onClick={closeModal}
          data-testid="cancelInviteCrewModal"
        >
          Cancel
        </StyledModalCancelButton>
      </ModalFooter>
    </Modal>
  );
};

InviteCrewModalUnconnected.propTypes = {
  isOpen: PropTypes.bool,
  team: PropTypes.object,
  clearModal: PropTypes.func,
  eventEligibleMembers: PropTypes.array,
  getEligibleMembers: PropTypes.func,
  isTeamAdmin: PropTypes.bool,
  inviteCrewByEmail: PropTypes.func,
};

const propMapper = ({ team, modal, eventEligibleMembers, isTeamAdmin }) => ({
  team,
  isTeamAdmin,
  isOpen: modal && modal.type === INVITE_CREW_MODAL,
  eventEligibleMembers,
});

export default connect(propMapper, {
  clearModal,
  getEligibleMembers,
  inviteCrewByEmail,
})(InviteCrewModalUnconnected);
